import { useCallback } from "react";
import { type AiCompletionPurpose, type AiItemDto, useCreateAiCompletionMutation } from "../../autogen/bff-api";
import { useRumError } from "../errors/RumComponentContext/useRumError";
import { useApiError } from "../errors/useApiError";
import { isAiNotAllowed } from "./aiNotAllowed";

export type CompletionMessage = { role: "SYSTEM" | "USER" | "ASSISTANT"; content: string };

export type StructuredCompletionRequest = {
  jsonSchema: string;
  messages: CompletionMessage[];
};

type UseAiCompletionProps = {
  purpose: AiCompletionPurpose;
  /** Tag for the Datadog error captured when a completion fails, e.g. "cpv-suggestions". Also sent
   *  to the endpoint for server-side logging. */
  feature: string;
  item?: AiItemDto;
};

const toWireMessages = (messages: CompletionMessage[]) =>
  messages.map((message) => ({ role: message.role.toLowerCase(), content: message.content }));

/**
 * Completions on the server-side AI layer (POST /api/ai/completions) — the own-layer replacement
 * for Fency's chat-completion agent tasks. The model is the environment's, not the caller's.
 */
export const useAiCompletion = ({ purpose, feature, item }: UseAiCompletionProps) => {
  const rumError = useRumError();
  const { trigger } = useApiError();
  const reportFailure = useCallback(
    (e: unknown, context: Record<string, unknown>) => {
      rumError(e, context);
      if (isAiNotAllowed(e)) trigger(e);
    },
    [rumError, trigger],
  );
  const [createCompletion] = useCreateAiCompletionMutation();

  const runStructured = useCallback(
    async (request: StructuredCompletionRequest, logContext?: Record<string, unknown>): Promise<string> => {
      try {
        const { content } = await createCompletion({
          createAiCompletionRequest: {
            purpose,
            feature,
            item,
            jsonSchema: request.jsonSchema,
            messages: toWireMessages(request.messages),
          },
        }).unwrap();
        return content;
      } catch (e) {
        reportFailure(e, { ...logContext, feature });
        throw e;
      }
    },
    [createCompletion, purpose, feature, item, reportFailure],
  );

  const runCompletion = useCallback(
    async (messages: CompletionMessage[], logContext?: Record<string, unknown>): Promise<string> => {
      try {
        const { content } = await createCompletion({
          createAiCompletionRequest: { purpose, feature, item, messages: toWireMessages(messages) },
        }).unwrap();
        return content;
      } catch (e) {
        reportFailure(e, { ...logContext, feature });
        throw e;
      }
    },
    [createCompletion, purpose, feature, item, reportFailure],
  );

  return { runStructured, runCompletion };
};

// Drop-in shape of the old fency hook: returns the structured runner directly.
export const useStructuredCompletion = (props: UseAiCompletionProps) => useAiCompletion(props).runStructured;
